import React, { useState } from 'react';
import Input from './Input';
import Checkbox from './Checkbox';
import { Icon } from '../icons';
import { emptyValueFor } from './filterModel';
import { useT } from '../../i18n';

/**
 * FilterValueEditor — renders the right editor for a filter field's type.
 * Live: calls onChange(nextValue) as the user edits.
 *
 * Reused by FilterPopover, FilterDrawer and the DataTable column menu so the
 * editing experience is identical everywhere.
 *
 * Props:
 *   field: { id, label, type, options? }
 *   value: current value (see filterModel for per-type shape)
 *   onChange: (nextValue) => void
 *   autoFocus: focus the first control
 */
const FilterValueEditor = ({ field, value, onChange, autoFocus = false }) => {
  const t = useT();
  const v = value ?? emptyValueFor(field);
  const [query, setQuery] = useState('');

  switch (field.type) {
    case 'select': {
      return (
        <div className="ps-fedit__options" role="radiogroup" aria-label={field.label}>
          {(field.options || []).map((opt) => {
            const active = String(v) === String(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                className={`ps-fedit__opt${active ? ' is-active' : ''}`}
                onClick={() => onChange(active ? '' : opt.value)}
              >
                <span className="ps-fedit__opt-label">{opt.label}</span>
                {active && <Icon.Check size={15} className="ps-fedit__opt-check" />}
              </button>
            );
          })}
        </div>
      );
    }

    case 'multiselect': {
      const arr = Array.isArray(v) ? v : [];
      const opts = field.options || [];
      const showSearch = field.searchable || opts.length > 6;
      const filtered = query
        ? opts.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
        : opts;
      const toggle = (val) => {
        const has = arr.some((x) => String(x) === String(val));
        onChange(has ? arr.filter((x) => String(x) !== String(val)) : [...arr, val]);
      };
      return (
        <div className="ps-fedit__multi">
          {showSearch && (
            <Input
              size="sm"
              autoFocus={autoFocus}
              leadingIcon={<Icon.Search />}
              placeholder={t('Search {field}…', { field: t(field.label).toLowerCase() })}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              wrapClassName="ps-fedit__search"
            />
          )}
          <div className="ps-fedit__options">
            {filtered.map((opt) => (
              <Checkbox
                key={opt.value}
                label={opt.label}
                checked={arr.some((x) => String(x) === String(opt.value))}
                onChange={() => toggle(opt.value)}
                className="ps-fedit__check"
              />
            ))}
            {filtered.length === 0 && <p className="ps-fedit__none">{t('No matches')}</p>}
          </div>
        </div>
      );
    }

    case 'daterange': {
      const dr = v || { from: '', to: '' };
      return (
        <div className="ps-fedit__range">
          <Input
            size="sm"
            type="date"
            label={t('From')}
            autoFocus={autoFocus}
            value={dr.from || ''}
            onChange={(e) => onChange({ ...dr, from: e.target.value })}
          />
          <Input
            size="sm"
            type="date"
            label={t('To')}
            value={dr.to || ''}
            onChange={(e) => onChange({ ...dr, to: e.target.value })}
          />
        </div>
      );
    }

    case 'date': {
      return (
        <Input
          size="sm"
          type="date"
          autoFocus={autoFocus}
          value={v || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }

    case 'numberrange': {
      const nr = v || { min: '', max: '' };
      return (
        <div className="ps-fedit__range">
          <Input
            size="sm"
            type="number"
            label={t('Min')}
            autoFocus={autoFocus}
            value={nr.min ?? ''}
            onChange={(e) => onChange({ ...nr, min: e.target.value })}
          />
          <Input
            size="sm"
            type="number"
            label={t('Max')}
            value={nr.max ?? ''}
            onChange={(e) => onChange({ ...nr, max: e.target.value })}
          />
        </div>
      );
    }

    case 'text':
    default:
      return (
        <Input
          size="sm"
          autoFocus={autoFocus}
          leadingIcon={<Icon.Search />}
          placeholder={t('Filter by {field}…', { field: t(field.label).toLowerCase() })}
          value={v || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

export default FilterValueEditor;
